import React from 'react'
import { ControlledModal } from '../../layouts/components/modal'
import { AppContext, Teacher, useAppContext } from '../../providers/context'
import { makeRequest, showAxiosErrorAsToast } from '../../request'
import { Button, ButtonStyle } from '../../components/simple/button'
import { BlueInput, TextInput } from '../../components/form/textInput'
import { Divider } from '../../components/simple/divider'
import { Toasts } from '../../providers/toast'

const CallerIDUpdateRow: React.FC<{
  name: string
  initialPhoneNumber: string
  placeholder: string
  id: number
  type: 'district' | 'school'
  reloadUser: AppContext['reloadUser']
}> = ({ name, placeholder, id, type, reloadUser, initialPhoneNumber }) => {
  const [phoneNumber, setPhoneNumber] = React.useState(initialPhoneNumber)
  const [loading, setLoading] = React.useState(false)
  const [pendingVerificationCode, setPendingVerificationCode] = React.useState<string | undefined>(undefined)

  const handleUpdatePhoneNumber = () => {
    setLoading(true)
    makeRequest<string>('/validateCallerID', {
      phoneNumber,
      type,
      id,
    })
      .then((response) => {
        if (response === 'invalid-phone') {
          Toasts.showToast({ message: 'Invalid phone number', error: true })
        } else if (response === 'already-verified') {
          Toasts.showToast({
            message: 'That number is already in use',
            error: true,
          })
        } else if (response === 'cleared') {
          Toasts.showToast({ message: 'Caller ID removed' })
          reloadUser()
        } else if (response === 'success') {
          Toasts.showToast({ message: 'Caller ID changed' })
          reloadUser()
        } else {
          setPendingVerificationCode(response)
        }
      })
      .catch(showAxiosErrorAsToast)
      .finally(() => {
        setLoading(false)
      })
  }

  const handleConfirmPhoneNumber = () => {
    setLoading(true)
    makeRequest<{ error: boolean; msg: string }>('/confirmCallerID', {
      phoneNumber,
    })
      .then((response) => {
        if (['invalid-phone', 'not-verified'].includes(response.msg)) {
          Toasts.showToast({
            message: 'Error validating number',
            error: true,
            duration: 10000,
          })
        } else if (response.error) {
          Toasts.showToast({
            message: response.msg,
            error: true,
            duration: 10000,
          })
        } else {
          Toasts.showToast({
            message: response.msg,
          })
          setPendingVerificationCode(undefined)
          reloadUser()
        }
      })
      .catch(showAxiosErrorAsToast)
      .finally(() => setLoading(false))
  }

  return (
    <div className="flex items-center gap-2">
      <b>{name}</b>
      {pendingVerificationCode ? (
        <>
          <div className="flex-grow text-nowrap rounded border-warning bg-gray-50 px-2 py-1.5">
            Answer the call to {phoneNumber} and enter code {pendingVerificationCode}
          </div>
          <Button buttonStyle={ButtonStyle.warning} disabled={loading} onClick={handleConfirmPhoneNumber}>
            Done
          </Button>
        </>
      ) : (
        <>
          <TextInput
            className={`${BlueInput} flex-grow`}
            value={phoneNumber}
            onChange={setPhoneNumber}
            placeholder={placeholder}
          />
          <Button logo disabled={loading || phoneNumber === initialPhoneNumber} onClick={handleUpdatePhoneNumber}>
            Update
          </Button>
        </>
      )}
    </div>
  )
}

export const CallerIDConfigModal: React.FC<{ user: Teacher; onClose: () => void }> = ({ user, onClose }) => {
  const { reloadUser } = useAppContext()
  const schoolsToShow = user.districtAccess ? user.district.schools : user.schools

  return (
    <ControlledModal
      size="lg"
      onOpenChange={(o) => {
        if (!o) onClose()
      }}
      open={true}
      title="School and District Caller ID"
    >
      Update the caller ID that is used for schools and districts.
      {user.districtAccess && user.admin > 0 && (
        <>
          <CallerIDUpdateRow
            name={user.district.name}
            initialPhoneNumber={user.district.phoneNumber}
            placeholder={'phone number'}
            id={user.district.id}
            type="district"
            reloadUser={reloadUser}
          />
          <Divider>SCHOOLS</Divider>
        </>
      )}
      {schoolsToShow.map((school) => (
        <CallerIDUpdateRow
          key={school.id}
          name={school.name}
          initialPhoneNumber={school.phoneNumber}
          placeholder={
            user.district.phoneNumber ? `inheriting ${user.district.phoneNumber} from district` : 'phone number'
          }
          id={school.id}
          type="school"
          reloadUser={reloadUser}
        />
      ))}
    </ControlledModal>
  )
}
